import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { CallButton, Eyebrow, GhostLink, MaskedLines, Reveal, EASE } from "./ui-bits";
import { HOURS, MARQUEE_ITEMS, SITE } from "../data/site";

export function PageHero({ eyebrow, lines, lede, image, imageAlt, secondary, tall = false }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  return (
    <section
      ref={ref}
      data-testid="page-hero"
      className={`relative flex items-end overflow-hidden ${tall ? "min-h-[92vh]" : "min-h-[68vh]"}`}
    >
      <motion.div style={{ y }} className="absolute inset-0 -bottom-24">
        <img src={image} alt={imageAlt} className="h-full w-full object-cover" fetchPriority="high" />
      </motion.div>
      <div
        className="absolute inset-0 bg-gradient-to-t from-ivory via-ivory/75 to-ivory/20"
        aria-hidden="true"
      />
      <div
        className="absolute inset-0 bg-gradient-to-r from-ivory/85 via-ivory/40 to-transparent"
        aria-hidden="true"
      />
      <div className="relative mx-auto w-full max-w-7xl px-5 pb-16 pt-40 sm:px-8 sm:pb-20">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.1 }}>
          <Eyebrow>{eyebrow}</Eyebrow>
        </motion.div>
        <h1 className="mt-5 max-w-3xl font-serif text-4xl font-medium leading-[1.08] tracking-tight text-plum sm:text-5xl lg:text-6xl">
          <MaskedLines lines={lines} />
        </h1>
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.7, ease: EASE }}
          className="mt-6 max-w-xl text-[17px] leading-relaxed text-charcoal sm:text-[18px]"
        >
          {lede}
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.9, ease: EASE }}
          className="mt-9 flex flex-wrap items-center gap-6"
        >
          <CallButton testid="hero-call-to-order" />
          {secondary && <GhostLink href={secondary.href} testid={secondary.testid}>{secondary.label}</GhostLink>}
        </motion.div>
      </div>
    </section>
  );
}

export function Marquee() {
  const row = [...MARQUEE_ITEMS, ...MARQUEE_ITEMS];
  return (
    <div data-testid="editorial-marquee" className="overflow-hidden border-y border-gold/25 bg-sagetint py-4" aria-hidden="true">
      <div className="flex w-max animate-jif-marquee items-center">
        {row.map((item, i) => (
          <span key={i} className="flex items-center">
            <span className="whitespace-nowrap px-8 font-serif text-lg italic text-plum">{item}</span>
            <span className="h-1.5 w-1.5 rotate-45 bg-gold" />
          </span>
        ))}
      </div>
    </div>
  );
}

export function HubCard({ href, image, imageAlt, eyebrow, title, blurb, testid }) {
  return (
    <Reveal>
      <Link to={href} data-testid={testid} className="group block">
        <div className="overflow-hidden rounded-xl">
          <img
            src={image}
            alt={imageAlt}
            loading="lazy"
            className="aspect-[4/3] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.05]"
          />
        </div>
        <p className="mt-5 text-xs font-semibold uppercase tracking-eyebrow text-gold">{eyebrow}</p>
        <h3 className="mt-2 font-serif text-2xl font-medium text-plum transition-colors duration-300 group-hover:text-plum">
          {title}
        </h3>
        <p className="mt-2 text-[15px] leading-relaxed text-charcoal/80">{blurb}</p>
        <span className="mt-3 inline-flex items-center gap-2 text-[13px] font-bold uppercase tracking-[0.14em] text-plum">
          Explore
          <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
        </span>
      </Link>
    </Reveal>
  );
}

export function CTABand({ title = "Ready to order? Call the shop.", note }) {
  return (
    <section data-testid="cta-band" className="bg-sagetint">
      <div className="mx-auto max-w-4xl px-5 py-20 text-center sm:px-8 sm:py-24">
        <Reveal>
          <Eyebrow align="center">Call to Order</Eyebrow>
          <h2 className="mt-5 font-serif text-3xl font-medium leading-snug text-plum sm:text-4xl">{title}</h2>
          <a
            href={SITE.phoneHref}
            data-testid="cta-band-phone"
            className="mt-6 inline-block font-serif text-4xl font-semibold tracking-tight text-plum transition-colors duration-300 hover:text-berry sm:text-5xl"
          >
            {SITE.phone}
          </a>
          <p className="mx-auto mt-5 max-w-lg text-[15px] leading-relaxed text-charcoal/75">
            {note ||
              "We take orders by phone or in person at the shop — no online ordering, just a real conversation with a florist who knows the flowers."}
          </p>
          <p className="mt-3 text-[13px] uppercase tracking-[0.2em] text-charcoal/50">
            {HOURS.map((h) => `${h.days} ${h.time}`).join(" · ")}
          </p>
        </Reveal>
      </div>
    </section>
  );
}

export function SectionHeading({ eyebrow, title, lede, align = "left" }) {
  return (
    <Reveal className={align === "center" ? "text-center" : ""}>
      <Eyebrow align={align}>{eyebrow}</Eyebrow>
      <h2 className="mt-4 max-w-2xl font-serif text-3xl font-medium leading-snug text-plum sm:text-4xl lg:text-[42px] lg:leading-[1.15]">
        {title}
      </h2>
      {lede && (
        <p className={`mt-4 max-w-xl text-[17px] leading-relaxed text-charcoal/85 ${align === "center" ? "mx-auto" : ""}`}>
          {lede}
        </p>
      )}
    </Reveal>
  );
}
